/**
 * Pattern: 9/21 EMA Cross (Diaz-style first-pullback + cross-price retest)
 *
 * JavaScript port of the user's NinjaTrader 8 indicator
 * `optimized_macross_alphawolf.cs` (default Chris config: fast=9 EMA,
 * slow=21 EMA). The 8th first-class pattern detector in JournifiAI,
 * sibling to Wolf Stack / Signal Bar / Holy Grail / 3-Bar Play / Turtle
 * Soup / Uno Reverse / H2-L2.
 *
 * Two entries of interest per cross:
 *
 *   - **Entry 1** ("5 EMA pullback") — Diaz's canonical first-pullback
 *     rule: after the 9/21 cross fires, the FIRST bar where price touches
 *     or pierces the 5 EMA in the cross direction AND closes back in the
 *     cross direction. The "wait for the pullback after trend
 *     confirmation" entry style.
 *
 *   - **Entry 2** ("LEVEL OF THE CROSS retest") — the price level where
 *     the 9 EMA numerically equals the 21 EMA (linearly interpolated
 *     between the bar BEFORE and the bar OF the cross) gets retested by
 *     a later bar's high-low range. The "let it come back" entry style.
 *
 * The detector reports the MOST RECENT cross within `lookbackBars`
 * (default 30) of the trade's entry bar. If multiple crosses occurred,
 * only the latest matters for this trade — older crosses are
 * superseded by the more recent regime change.
 *
 * Additionally classifies whether the trade's entry bar coincides with
 * Entry 1 or Entry 2 (within 1 bar of tolerance), so trade-card chips
 * can label *what kind* of entry it was, not just "a cross happened."
 *
 * Pure function. No side effects. Composes with v7.5.13 direction-cohort
 * (the `alignsWithTrade` field lets cohort builders filter naturally)
 * and v7.5.14 live-trade gate (no cohort outcome claims made here).
 */

import { computeEMA } from '../emas.js';

const DEFAULT_OPTS = Object.freeze({
  fastPeriod: 9,
  slowPeriod: 21,
  pullbackPeriod: 5,
  /** Bars before the entry bar to scan for the most-recent cross. */
  lookbackBars: 30,
  /** Trade entry counts as Entry 1 / Entry 2 if its bar index is within
   *  this distance of the entry-bar marker. 1 = same or adjacent bar. */
  entryToleranceBars: 1,
});

const normalizeDirection = (raw) => {
  if (!raw) return null;
  const s = String(raw).toLowerCase().trim();
  if (s === 'long' || s === 'buy' || s === 'l') return 'long';
  if (s === 'short' || s === 'sell' || s === 's') return 'short';
  return null;
};

const findEntryBarIdx = (bars, entryTs) => {
  if (!Array.isArray(bars) || bars.length === 0) return -1;
  let idx = -1;
  for (let i = 0; i < bars.length; i++) {
    const t = Number(bars[i]?.time);
    if (!Number.isFinite(t)) continue;
    if (t <= entryTs) idx = i;
    else break;
  }
  return idx;
};

/**
 * Linearly interpolate the price at which `fast` and `slow` were equal
 * between bar[i-1] and bar[i]. The cross happens during the bar interval
 * — we don't have intra-bar data so the linear-interp is the best proxy.
 *
 * Math: solve for t in [0, 1] where
 *   fastPrev + t·(fastNow − fastPrev) == slowPrev + t·(slowNow − slowPrev)
 *   t = (slowPrev − fastPrev) / ((fastNow − fastPrev) − (slowNow − slowPrev))
 * Then crossPrice = fastPrev + t · (fastNow − fastPrev).
 *
 * Degenerate cases (parallel lines, zero denominator) fall back to the
 * mean of the four values — close enough when the lines barely diverged.
 */
const interpolateCrossPrice = (fastPrev, slowPrev, fastNow, slowNow) => {
  if (![fastPrev, slowPrev, fastNow, slowNow].every(Number.isFinite)) return null;
  const dFast = fastNow - fastPrev;
  const dSlow = slowNow - slowPrev;
  const denom = dFast - dSlow;
  if (Math.abs(denom) < 1e-9) {
    return (fastPrev + slowPrev + fastNow + slowNow) / 4;
  }
  const t = (slowPrev - fastPrev) / denom;
  return fastPrev + t * dFast;
};

/**
 * Detect the most recent 9/21 EMA cross within the lookback window of
 * `entryTs`, classify Entry 1 + Entry 2 status, and report whether the
 * trade's actual entry coincides with either.
 *
 * @param {Object} params
 * @param {Array}  params.bars       OHLC bars (must have time + close fields).
 * @param {number} params.entryTs    Trade entry Unix timestamp (seconds).
 * @param {number} params.entryPrice Trade entry price (unused by the
 *                                   detector, kept for signature
 *                                   compatibility with sibling detectors).
 * @param {string} params.direction  Trade direction (long/short/aliases).
 * @param {Object} [params.options]  Override DEFAULT_OPTS fields.
 * @returns {{
 *   type: '9-21-cross',
 *   direction: 'long' | 'short',
 *   alignsWithTrade: boolean,
 *   crossBarIndex: number,
 *   crossPrice: number,
 *   barsSinceCross: number,
 *   entry1Fired: boolean,
 *   entry1BarIndex: number | null,
 *   entry2Fired: boolean,
 *   entry2BarIndex: number | null,
 *   enteredAtEntry1: boolean,
 *   enteredAtEntry2: boolean,
 *   details: string,
 * } | null}
 *   Null when no cross is found in the lookback window OR inputs are
 *   insufficient (no bars, no direction, no entry timestamp).
 */
export const detectMaCross921 = ({ bars, entryTs, entryPrice, direction, options = {} } = {}) => {
  const tradeDir = normalizeDirection(direction);
  if (!Array.isArray(bars) || bars.length === 0) return null;
  if (!Number.isFinite(entryTs)) return null;

  const fastPeriod = Math.floor(Number(options.fastPeriod ?? DEFAULT_OPTS.fastPeriod));
  const slowPeriod = Math.floor(Number(options.slowPeriod ?? DEFAULT_OPTS.slowPeriod));
  const pullbackPeriod = Math.floor(Number(options.pullbackPeriod ?? DEFAULT_OPTS.pullbackPeriod));
  const lookbackBars = Math.max(2, Math.floor(Number(options.lookbackBars ?? DEFAULT_OPTS.lookbackBars)));
  const entryTolerance = Math.max(0, Math.floor(Number(options.entryToleranceBars ?? DEFAULT_OPTS.entryToleranceBars)));

  const entryIdx = findEntryBarIdx(bars, entryTs);
  if (entryIdx < 1) return null;

  // Compute EMAs once across the whole bars array — single allocation per
  // detector run. Within the lookback we read values back by index.
  const fastSeries = computeEMA(bars, fastPeriod);
  const slowSeries = computeEMA(bars, slowPeriod);
  const pullbackSeries = computeEMA(bars, pullbackPeriod);

  // Scan backwards from the entry bar for the most-recent cross. We walk
  // the window [entryIdx − lookbackBars + 1 ... entryIdx] and look for a
  // sign change in (fast − slow) from one bar to the next.
  const windowStart = Math.max(1, entryIdx - lookbackBars + 1);
  let crossBarIndex = -1;
  let crossDirection = null;
  let crossPrice = null;

  for (let i = entryIdx; i >= windowStart; i--) {
    const fPrev = fastSeries[i - 1]?.value;
    const sPrev = slowSeries[i - 1]?.value;
    const fNow = fastSeries[i]?.value;
    const sNow = slowSeries[i]?.value;
    if (![fPrev, sPrev, fNow, sNow].every(Number.isFinite)) continue;

    // Bullish cross: fast crosses ABOVE slow.
    if (fPrev <= sPrev && fNow > sNow) {
      crossBarIndex = i;
      crossDirection = 'long';
      crossPrice = interpolateCrossPrice(fPrev, sPrev, fNow, sNow);
      break;
    }
    // Bearish cross: fast crosses BELOW slow.
    if (fPrev >= sPrev && fNow < sNow) {
      crossBarIndex = i;
      crossDirection = 'short';
      crossPrice = interpolateCrossPrice(fPrev, sPrev, fNow, sNow);
      break;
    }
  }

  if (crossBarIndex < 0 || !crossDirection || !Number.isFinite(crossPrice)) {
    return null;
  }

  // Scan forward from the cross looking for Entry 1 + Entry 2 — only up
  // to (and including) the trade's entry bar. Anything past the entry bar
  // hasn't happened yet from the trade's perspective.
  let entry1Fired = false;
  let entry1BarIndex = null;
  let entry2Fired = false;
  let entry2BarIndex = null;

  for (let i = crossBarIndex + 1; i <= entryIdx; i++) {
    const bar = bars[i];
    if (!bar) continue;
    const high = Number(bar.high);
    const low = Number(bar.low);
    const close = Number(bar.close);
    const ema5 = pullbackSeries[i]?.value;

    // Entry 1 — Diaz first-pullback to 5 EMA in cross direction.
    // For LONG: bar's low touched/pierced the 5 EMA AND close held above it.
    // For SHORT: mirror.
    if (!entry1Fired && Number.isFinite(ema5)) {
      const touched5 = crossDirection === 'long'
        ? Number.isFinite(low) && low <= ema5 && Number.isFinite(close) && close > ema5
        : Number.isFinite(high) && high >= ema5 && Number.isFinite(close) && close < ema5;
      if (touched5) {
        entry1Fired = true;
        entry1BarIndex = i;
      }
    }

    // Entry 2 — retest of the interpolated cross price.
    // Fires when a later bar's high-low range contains the cross price.
    if (!entry2Fired && Number.isFinite(high) && Number.isFinite(low)) {
      if (low <= crossPrice && high >= crossPrice) {
        entry2Fired = true;
        entry2BarIndex = i;
      }
    }

    if (entry1Fired && entry2Fired) break;
  }

  // Classify whether the trade's actual entry bar coincides with either
  // entry style. "Within N bars" tolerance — strict equality is too brittle
  // (entry might land on the bar just after the signal).
  const enteredAtEntry1 = entry1Fired
    && Math.abs(entryIdx - entry1BarIndex) <= entryTolerance;
  const enteredAtEntry2 = entry2Fired
    && Math.abs(entryIdx - entry2BarIndex) <= entryTolerance;

  const alignsWithTrade = tradeDir != null && tradeDir === crossDirection;

  // Human-readable details — feeds the chip tooltip + Coach observation
  // detail text per the existing pattern-detector convention.
  const entryLabel = enteredAtEntry1
    ? 'Entry 1 (5 EMA pullback)'
    : enteredAtEntry2
      ? 'Entry 2 (cross-price retest)'
      : null;
  const detailsBits = [
    `${crossDirection.toUpperCase()} cross ${entryIdx - crossBarIndex} bar(s) before entry @ ~${crossPrice.toFixed(2)}`,
    entry1Fired ? `Entry 1 fired bar ${entry1BarIndex}` : 'Entry 1 not fired',
    entry2Fired ? `Entry 2 fired bar ${entry2BarIndex}` : 'Entry 2 not fired',
    entryLabel ? `Trade entered at ${entryLabel}` : null,
  ].filter(Boolean);
  const details = detailsBits.join('; ');

  return {
    type: '9-21-cross',
    direction: crossDirection,
    alignsWithTrade,
    crossBarIndex,
    crossPrice,
    barsSinceCross: entryIdx - crossBarIndex,
    entry1Fired,
    entry1BarIndex,
    entry2Fired,
    entry2BarIndex,
    enteredAtEntry1,
    enteredAtEntry2,
    details,
  };
};

// Test-friendly exports.
export {
  interpolateCrossPrice,
  normalizeDirection,
  DEFAULT_OPTS,
};
